import React from "react";
import { FormattedMessage, IntlProvider } from "react-intl";
import { confirmAlert } from "react-confirm-alert";
import PropTypes from "prop-types";
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css

import messagesFr from "workspace/translations/fr";
import messagesEn from "workspace/translations/en";
import Button from "common/components/Button";
import "common/components/ModalDialog.css"; // Import css

const messages = {
  fr: messagesFr,
  en: messagesEn
};

function onClickFactory(handler, close) {
  return () => {
    if (handler) {
      handler();
    }
    close();
  };
}

/**
 * Use this factory when creating modal windows. It will return an callback
 * you can call when the modal needs to open.
 * @param  {[object]} props Properties to pass to the modal window. See propTypes for ModalDialog component.
 * @return {[function]} A callback function which opens the modal window
 */
export const modalFactory = props => {
  const modal = event => {
    event.preventDefault();
    const customUi = ({ onClose }) => {
      const onConfirm = onClickFactory(props.confirm.handler, onClose);
      const onReject = onClickFactory(props.reject.handler, onClose);

      return (
        <ModalDialog
          dialog={props.dialog}
          confirm={{ ...props.confirm, onClick: onConfirm }}
          reject={{ ...props.reject, onClick: onReject }}
          onClose={onClose}
          customUI={props.customUI}
        />
      );
    };
    confirmAlert({
      customUI: customUi
    });
  };
  return modal;
};

/**
 * A reusable modal dialog window with customizable title and body UI.
 * Note: Don't create this modal directly, use the modalFactory instead.
 */
function ModalDialog({ dialog, customUI, confirm, reject }) {
  let bodyDialog = "";
  if (dialog.body) {
    bodyDialog = <FormattedMessage id={dialog.body} />;
  }
  return (
    // HACK ALERT: Due to the way this component renders and places its contents in the DOM,
    // it does not have access to the react-intl provider. So to get around this, re-wrap its contents
    // in another provider pointing to the same locale and message files as the parent.
    <IntlProvider
      locale={window.settings.language}
      messages={messages[window.settings.language]}
    >
      <div className="modal">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                <FormattedMessage id={dialog.title} />
              </h2>
            </div>
            <div className="modal-body">
              {bodyDialog}
              {customUI}
            </div>
            <div className="modal-footer">
              <Button action={reject} />
              <Button action={confirm} />
            </div>
          </div>
        </div>
      </div>
    </IntlProvider>
  );
}

ModalDialog.propTypes = {
  // Object containing 'title' and 'body' translation key strings.
  dialog: PropTypes.object.isRequired,
  // A react UI element to be inserted into the body
  customUI: PropTypes.element,
  // Callback when user clicks the ok/confirm button
  confirm: PropTypes.func,
  // Callback when user clicks the cancel button
  reject: PropTypes.func
};
